'use client';

import React, { useState, useEffect } from 'react';
import { useRouter } from 'next/navigation';
import Icon from '@/components/ui/AppIcon';
import { trackFormSubmission } from '@/lib/analytics';

const WEB3FORMS_ACCESS_KEY = '3bff64b8-4d4c-4714-878d-e786ed51fdf4';

interface ServiceRequestFormProps {
  selectedFormula?: string;
}

interface FormData {
  fullName: string;
  email: string;
  phone: string;
  country: string;
  city: string;
  formula: string;
  delay: string;
  priority: string;
  message: string;
  consent: boolean;
}

const ServiceRequestForm = ({ selectedFormula = '' }: ServiceRequestFormProps) => {
  const router = useRouter();
  const [formData, setFormData] = useState<FormData>({
    fullName: '',
    email: '',
    phone: '',
    country: '',
    city: '',
    formula: selectedFormula,
    delay: '',
    priority: '',
    message: '',
    consent: false,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [errors, setErrors] = useState<Partial<FormData>>({});

  useEffect(() => {
    if (selectedFormula) {
      setFormData(prev => ({ ...prev, formula: selectedFormula }));
    }
  }, [selectedFormula]);

  const validate = (): boolean => {
    const newErrors: Partial<FormData> = {};
    if (!formData.fullName.trim()) newErrors.fullName = 'Le nom complet est obligatoire';
    if (!formData.email.trim()) newErrors.email = "L'email est obligatoire";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) newErrors.email = 'Email invalide';
    if (!formData.consent) newErrors.consent = "Vous devez accepter d'être recontacté" as any;
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
    if (errors[name as keyof FormData]) {
      setErrors(prev => ({ ...prev, [name]: undefined }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    setIsSubmitting(true);
    setSubmitError(null);

    try {
      const payload = {
        access_key: WEB3FORMS_ACCESS_KEY,
        subject: "Nouvelle demande – S\'installer au Maroc",
        from_name: formData.fullName,
        replyto: formData.email,
        // All named fields
        name: formData.fullName,
        email: formData.email,
        phone: formData.phone || 'Non renseigné',
        country: formData.country || 'Non renseigné',
        city: formData.city || 'Non renseigné',
        formula: formData.formula || 'Non renseigné',
        delay: formData.delay || 'Non renseigné',
        priority: formData.priority || 'Non renseigné',
        message: formData.message || 'Aucun message',
        botcheck: false,
      };

      console.log('[ServiceRequestForm] Submitting directly to Web3Forms (client-side)...');

      const res = await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(payload),
      });

      let data: Record<string, unknown> = {};
      try {
        data = await res.json();
      } catch (parseErr) {
        console.error('[ServiceRequestForm] Failed to parse Web3Forms response. HTTP status:', res.status, parseErr);
        setSubmitError(`Email service returned invalid response (HTTP ${res.status})`);
        return;
      }

      console.log('[ServiceRequestForm] Web3Forms response (HTTP', res.status, '):', JSON.stringify(data));

      if (data.success) {
        trackFormSubmission({
          form_id: 'service_request_form',
          formula: formData.formula,
          delay: formData.delay,
          priority: formData.priority,
          country: formData.country,
        });
        const params = new URLSearchParams();
        if (formData.fullName) params.set('name', formData.fullName);
        if (formData.formula) params.set('formula', formData.formula);
        if (formData.delay) params.set('delay', formData.delay);
        if (formData.priority) params.set('priority', formData.priority);
        if (formData.country) params.set('country', formData.country);
        router.push(`/merci?${params.toString()}`);
      } else {
        const errMsg = (data.message as string) || 'Une erreur est survenue. Veuillez réessayer.';
        console.error('[ServiceRequestForm] Web3Forms error:', errMsg, '| HTTP status:', res.status, '| Full response:', JSON.stringify(data));
        setSubmitError(errMsg);
      }
    } catch (err) {
      console.error('[ServiceRequestForm] Network error submitting to Web3Forms:', err);
      setSubmitError('Une erreur réseau est survenue. Vérifiez votre connexion et réessayez.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const inputClass = (field: keyof FormData) =>
    `w-full px-4 py-3 rounded-lg border ${
      errors[field] ? 'border-red-400 bg-red-50' : 'border-border bg-white'
    } text-text-primary placeholder-text-secondary focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-all duration-200 text-sm`;

  const labelClass = 'block text-sm font-semibold text-text-primary mb-1.5';

  return (
    <section id="demande-service" className="py-20 bg-gradient-to-br from-[#fdf2f2] to-white">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 bg-primary/10 text-primary px-4 py-2 rounded-full text-sm font-semibold mb-4">
            <Icon name="EnvelopeIcon" size={16} />
            Formulaire de demande
          </div>
          <h2 className="text-3xl lg:text-4xl font-poppins font-bold text-text-primary mb-4 leading-tight">
            Parlez-nous de votre projet<br />
            <span className="text-primary">d'installation au Maroc</span>
          </h2>
          <p className="text-lg text-text-secondary max-w-2xl mx-auto leading-relaxed">
            Remplissez ce formulaire et notre équipe vous recontactera rapidement avec une solution adaptée à votre situation.
          </p>
        </div>

        {/* Form Card */}
        <div className="bg-white rounded-2xl shadow-morocco border border-primary/10 overflow-hidden">
          {/* Red top bar */}
          <div className="h-1.5 bg-gradient-to-r from-primary via-[#e63950] to-primary" />

          <form onSubmit={handleSubmit} className="p-8 sm:p-10 space-y-6">
            {/* Hidden Web3Forms fields */}
            <input type="hidden" name="access_key" value={WEB3FORMS_ACCESS_KEY} />
            <input type="hidden" name="subject" value="Nouvelle demande – S'installer au Maroc" />
            <input type="hidden" name="replyto" value={formData.email} />
            <input type="checkbox" name="botcheck" className="hidden" />

            {/* Row 1: Nom + Email */}
            <div className="grid sm:grid-cols-2 gap-5">
              <div>
                <label className={labelClass}>
                  Nom complet <span className="text-primary">*</span>
                </label>
                <input
                  type="text"
                  name="fullName"
                  value={formData.fullName}
                  onChange={handleChange}
                  placeholder="Jean Dupont"
                  className={inputClass('fullName')}
                />
                {errors.fullName && <p className="mt-1 text-xs text-red-500">{errors.fullName}</p>}
              </div>
              <div>
                <label className={labelClass}>
                  Email <span className="text-primary">*</span>
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="jean@exemple.com"
                  className={inputClass('email')}
                />
                {errors.email && <p className="mt-1 text-xs text-red-500">{errors.email}</p>}
              </div>
            </div>

            {/* Row 2: Téléphone + Pays */}
            <div className="grid sm:grid-cols-2 gap-5">
              <div>
                <label className={labelClass}>Téléphone / WhatsApp</label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder="+33 6 00 00 00 00"
                  className={inputClass('phone')}
                />
              </div>
              <div>
                <label className={labelClass}>Pays de résidence actuel</label>
                <input
                  type="text"
                  name="country"
                  value={formData.country}
                  onChange={handleChange}
                  placeholder="France"
                  className={inputClass('country')}
                />
              </div>
            </div>

            {/* Row 3: Ville + Formule */}
            <div className="grid sm:grid-cols-2 gap-5">
              <div>
                <label className={labelClass}>Ville souhaitée au Maroc</label>
                <input
                  type="text"
                  name="city"
                  value={formData.city}
                  onChange={handleChange}
                  placeholder="Casablanca, Marrakech..."
                  className={inputClass('city')}
                />
              </div>
              <div>
                <label className={labelClass}>Formule choisie</label>
                <div className="relative">
                  <input
                    type="text"
                    name="formula"
                    value={formData.formula}
                    onChange={handleChange}
                    placeholder="Sélectionnez une formule ci-dessus"
                    className={`${inputClass('formula')} ${formData.formula ? 'bg-primary/5 border-primary/40 font-medium text-primary' : ''}`}
                    readOnly={!!formData.formula}
                  />
                  {formData.formula && (
                    <div className="absolute right-3 top-1/2 -translate-y-1/2">
                      <Icon name="CheckCircleIcon" size={18} className="text-primary" />
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Row 4: Délai + Priorité */}
            <div className="grid sm:grid-cols-2 gap-5">
              <div>
                <label className={labelClass}>Délai du projet</label>
                <select
                  name="delay"
                  value={formData.delay}
                  onChange={handleChange}
                  className={inputClass('delay')}
                >
                  <option value="">Sélectionner...</option>
                  <option value="0-3 mois">0 à 3 mois</option>
                  <option value="3-6 mois">3 à 6 mois</option>
                  <option value="6-12 mois">6 à 12 mois</option>
                  <option value="plus d'un an">Plus d'un an</option>
                </select>
              </div>
              <div>
                <label className={labelClass}>Votre priorité</label>
                <select
                  name="priority"
                  value={formData.priority}
                  onChange={handleChange}
                  className={inputClass('priority')}
                >
                  <option value="">Sélectionner...</option>
                  <option value="logement">Logement</option>
                  <option value="création d'entreprise">Création d'entreprise</option>
                  <option value="démarches administratives">Démarches administratives</option>
                  <option value="fiscalité">Fiscalité</option>
                  <option value="investissement immobilier">Investissement immobilier</option>
                  <option value="accompagnement complet">Accompagnement complet</option>
                </select>
              </div>
            </div>

            {/* Message */}
            <div>
              <label className={labelClass}>Message libre</label>
              <textarea
                name="message"
                value={formData.message}
                onChange={handleChange}
                rows={4}
                placeholder="Décrivez votre projet, vos questions, vos contraintes particulières..."
                className={`${inputClass('message')} resize-none`}
              />
            </div>

            {/* Consent */}
            <div className="bg-gray-50 rounded-xl p-4 border border-border">
              <label className="flex items-start gap-3 cursor-pointer group">
                <div className="relative mt-0.5 flex-shrink-0">
                  <input
                    type="checkbox"
                    name="consent"
                    checked={formData.consent}
                    onChange={handleChange}
                    className="sr-only"
                  />
                  <div className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-all duration-200 ${
                    formData.consent ? 'bg-primary border-primary' : 'border-border bg-white group-hover:border-primary/50'
                  }`}>
                    {formData.consent && <Icon name="CheckIcon" size={12} className="text-white" />}
                  </div>
                </div>
                <span className="text-sm text-text-secondary leading-relaxed">
                  J'accepte d'être recontacté concernant mon projet d'installation au Maroc.
                </span>
              </label>
              {errors.consent && <p className="mt-2 text-xs text-red-500 ml-8">{String(errors.consent)}</p>}
            </div>

            {/* Submit */}
            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full py-4 px-8 bg-primary hover:bg-primary/90 text-white font-poppins font-bold text-base rounded-xl shadow-morocco hover:shadow-morocco-lg transform hover:scale-[1.02] transition-all duration-300 disabled:opacity-60 disabled:cursor-not-allowed disabled:transform-none flex items-center justify-center gap-3"
            >
              {isSubmitting ? (
                <>
                  <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24" fill="none">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 12 0 12 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  Envoi en cours...
                </>
              ) : (
                <>
                  <Icon name="PaperAirplaneIcon" size={20} />
                  Envoyer ma demande
                </>
              )}
            </button>

            {/* Inline error message */}
            {submitError && (
              <div className="flex items-start gap-3 bg-red-50 border border-red-200 rounded-xl p-4">
                <Icon name="ExclamationCircleIcon" size={20} className="text-red-500 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-semibold text-red-700">Une erreur est survenue</p>
                  <p className="text-xs text-red-600 mt-0.5">{submitError}</p>
                </div>
              </div>
            )}

            <p className="text-center text-xs text-text-secondary">
              <Icon name="LockClosedIcon" size={12} className="inline mr-1" />
              Vos données sont confidentielles et ne seront jamais partagées avec des tiers.
            </p>
          </form>
        </div>
      </div>
    </section>
  );
};

export default ServiceRequestForm;
